import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  untracked,
  ChangeDetectionStrategy,
  linkedSignal,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { form, minLength, required, validate } from "@angular/forms/signals";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { AmcAnalysisTemplate, AmcAnalysisTemplateOption } from "@front/m19-api-client";
import { AccountSelectionService, StrategyCache } from "@front/m19-services";
import { IAlertComponent, IButtonDirective, IStepperComponent, SpinnerComponent } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { DateTime } from "luxon";
import { combineLatestWith, filter, map, switchMap } from "rxjs";
import {
  AmcStrategyAnalysisService,
  DeliveredAmcAnalysisStrategy,
} from "@m19-board/amc/amc-strategy-analysis-service/amc-strategy-analysis.service";
import {
  AmcAnalysisPeriodModel,
  AmcAnalysisPeriodStepComponent,
  AmcAnalysisPeriodWindow,
} from "@m19-board/amc/amc-strategy-analysis-creation-page/steps/amc-analysis-period-step.component";
import {
  AmcAnalysisStrategyModel,
  AmcAnalysisStrategyStepComponent,
} from "@m19-board/amc/amc-strategy-analysis-creation-page/steps/amc-analysis-strategy-step.component";
import {
  AmcAnalysisTemplateModel,
  AmcAnalysisTemplateStepComponent,
} from "@m19-board/amc/amc-strategy-analysis-creation-page/steps/amc-analysis-template-step.component";
import { FormNavigationService, IFormStep } from "@m19-board/shared/form-navigation/form-navigation.service";
import { validateCompleteDateRange } from "@m19-board/utils/validators/dateRangeValidators";
import { FormShellComponent } from "@m19-board/shared/form-shell/form-shell.component";
import { NavBreadcrumbDirective } from "@m19-board/containers/page-layout/nav-breadcrumb.directive";

interface AmcAnalysisCreationFormModel {
  strategy: AmcAnalysisStrategyModel;
  template: AmcAnalysisTemplateModel;
  period: AmcAnalysisPeriodModel;
}

export function periodWindow(strategy: DeliveredAmcAnalysisStrategy, today: DateTime): AmcAnalysisPeriodWindow {
  return {
    min: DateTime.fromISO(strategy.firstDeliveryDate, { zone: "utc" }),
    max: DateTime.min(DateTime.fromISO(strategy.lastDeliveryDate, { zone: "utc" }), today),
  };
}

export function incompleteResultTemplates(
  options: AmcAnalysisTemplateOption[],
  periodEnd: DateTime,
  today: DateTime,
): AmcAnalysisTemplate[] {
  const daysSinceEnd = today.diff(periodEnd.startOf("day"), "days").days;
  return options.filter((o) => o.minDaysAfterEnd > daysSinceEnd).map((o) => o.template);
}

@Component({
  selector: "app-amc-strategy-analysis-creation-page",
  providers: [FormNavigationService],
  imports: [
    TranslocoDirective,
    FormShellComponent,
    IStepperComponent,
    IButtonDirective,
    IAlertComponent,
    SpinnerComponent,
    AmcAnalysisStrategyStepComponent,
    AmcAnalysisTemplateStepComponent,
    AmcAnalysisPeriodStepComponent,
    FormShellComponent,
    NavBreadcrumbDirective,
    RouterLink,
  ],
  templateUrl: "./amc-strategy-analysis-creation-page.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AmcStrategyAnalysisCreationPageComponent {
  private readonly amcStrategyAnalysisService = inject(AmcStrategyAnalysisService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly strategyCache = inject(StrategyCache);
  private readonly translocoService = inject(TranslocoService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formNavigationService = inject(FormNavigationService<AmcAnalysisCreationFormModel>);

  private readonly today = DateTime.utc().startOf("day");

  private readonly strategyId$ = this.route.paramMap.pipe(
    map((params) => params.get("id")),
    filter((strategyId): strategyId is string => !!strategyId),
    map((strategyId) => +strategyId),
  );
  readonly strategy = toSignal(
    this.strategyId$.pipe(
      combineLatestWith(this.accountSelectionService.singleAccountMarketplaceSelection$),
      switchMap(([strategyId, am]) =>
        this.strategyCache
          .getAllStrategyIndex(am.accountId, am.marketplace)
          .pipe(map((strategies) => strategies.get(strategyId))),
      ),
    ),
  );
  private readonly preselectedStrategy = toSignal(
    this.strategyId$.pipe(switchMap((strategyId) => this.amcStrategyAnalysisService.getDspStrategy(strategyId))),
  );

  private readonly formModel = linkedSignal<AmcAnalysisCreationFormModel>(() => ({
    strategy: { strategyId: this.preselectedStrategy()?.strategyId ?? null },
    template: { templates: [], brandKeywords: [] },
    period: { period: null },
  }));

  readonly creationForm = form(
    this.formModel,
    (schema) => {
      required(schema.strategy.strategyId);
      minLength(schema.template.templates, 1);

      required(schema.period.period);
      validate(schema.period.period, validateCompleteDateRange);
    },
    { submission: { action: async () => this.submit() } },
  );

  readonly dspAdvertiserId = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(map((am) => am.dspAdvertiserId ?? null)),
  );

  readonly dspStrategies = toSignal(this.amcStrategyAnalysisService.dspStrategies$);
  private readonly selectedStrategy = computed(() => {
    const strategyId = this.creationForm.strategy.strategyId().value();
    return this.dspStrategies()?.find((s) => s.strategyId === strategyId);
  });

  readonly templateOptions = computed(() => this.selectedStrategy()?.templates.map((o) => o.template) ?? []);

  readonly selectedPeriodWindow = computed(() => {
    const strategy = this.selectedStrategy();
    return strategy ? periodWindow(strategy, this.today) : undefined;
  });

  readonly selectedIncompleteResultTemplates = computed(() => {
    const period = this.creationForm.period.period().value();
    if (!period) {
      return [];
    }
    const templates = this.creationForm.template.templates().value();
    const options = this.selectedStrategy()?.templates.filter((o) => templates.includes(o.template)) ?? [];
    return incompleteResultTemplates(options, period.end ?? period.start, this.today);
  });

  readonly isCreating = signal(false);

  readonly currentStepId = this.formNavigationService.currentStepId;
  readonly formSteps = this.formNavigationService.formSteps;
  readonly isFirstStep = this.formNavigationService.isFirstStep;
  readonly isLastStep = this.formNavigationService.isLastStep;
  readonly canGoNext = this.formNavigationService.canGoNext;

  readonly showStrategyStep = computed(() => !this.strategy());

  constructor() {
    this.formNavigationService.configure({
      steps: [
        {
          stepId: "strategy",
          label: this.translocoService.translate("common.strategy"),
          size: "sm",
          showWhen: this.showStrategyStep,
        },
        { stepId: "template", label: this.translocoService.translate("common.template"), size: "sm" },
        { stepId: "period", label: this.translocoService.translate("common.period"), size: "sm" },
      ],
      form: this.creationForm,
      blocked: this.isCreating,
    });

    effect(() => {
      this.creationForm.strategy.strategyId().value();
      untracked(() => {
        this.formNavigationService.updateReachedStepIds(["strategy"]);
        this.creationForm.template.templates().reset([]);
        this.creationForm.period.period().reset(null);
      });
    });
  }

  goToStep(stepId: IFormStep<AmcAnalysisCreationFormModel>) {
    this.formNavigationService.goToStep(stepId);
  }

  back() {
    this.formNavigationService.back();
  }

  next() {
    this.formNavigationService.next();
  }

  cancel() {
    this.navigateBack();
  }

  private submit() {
    const dspAdvertiserId = this.dspAdvertiserId();
    const strategy = this.selectedStrategy();
    const templates = this.creationForm.template.templates().value();
    const period = this.creationForm.period.period().value();
    if (this.creationForm().invalid() || this.isCreating() || !dspAdvertiserId || !strategy || !period) {
      return;
    }
    this.isCreating.set(true);
    this.amcStrategyAnalysisService
      .create({
        strategyIds: [strategy.strategyId],
        templates,
        startDate: period.start.toISODate()!,
        endDate: (period.end ?? period.start).toISODate()!,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.navigateBack(),
        error: () => this.isCreating.set(false),
      });
  }

  private navigateBack() {
    this.router.navigate([".."], { relativeTo: this.route, queryParamsHandling: "merge" });
  }
}
